import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LoadingOverlay, useAlert } from '../../components';
import { getBankDetails, createWithdrawalRequest } from '../../services/firestoreService';
import { formatCurrency, maskAccountNumber, isValidFundPassword } from '../../utils/helpers';
import './WithdrawScreen.css';

const MIN_WITHDRAWAL = 110;
const WITHDRAWAL_TAX = 6; // Matching reference image (6%)

export const WithdrawScreen = () => {
    const navigate = useNavigate();
    const { userData, refreshUserData } = useAuth();
    const alert = useAlert();

    const [amount, setAmount] = useState('');
    const [fundPassword, setFundPassword] = useState('');
    const [bankDetails, setBankDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadBankDetails();
    }, []);

    const loadBankDetails = async () => {
        try {
            const data = await getBankDetails();
            setBankDetails(data);
        } catch (error) {
            console.error('Error loading bank details:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculateNetAmount = () => {
        const amountNum = parseInt(amount) || 0;
        const tax = (amountNum * WITHDRAWAL_TAX) / 100;
        return amountNum - tax;
    };

    const handleWithdraw = async () => {

        if (!bankDetails) {
            alert.showConfirm(
                'Bank Details Required',
                'Please add your bank details to proceed with withdrawal.',
                () => navigate('/bank-details')
            );
            return;
        }

        if (!userData?.fundPassword) {
            alert.showConfirm(
                'Fund Password Required',
                'Please set your fund password in Security settings.',
                () => navigate('/security')
            );
            return;
        }

        const amountNum = parseInt(amount);
        if (!amount || isNaN(amountNum) || amountNum < MIN_WITHDRAWAL) {
            alert.showError('Error', `Minimum withdrawal is ${formatCurrency(MIN_WITHDRAWAL)}`);
            return;
        }

        if (amountNum > (userData?.withdrawBalance || 0)) {
            alert.showError('Error', 'Insufficient balance');
            return;
        }

        if (!fundPassword || !isValidFundPassword(fundPassword)) {
            alert.showError('Error', 'Invalid fund password (must be 6 digits)');
            return;
        }

        setSubmitting(true);
        try {
            await createWithdrawalRequest(amountNum, bankDetails.id, fundPassword);
            await refreshUserData();
            alert.showSuccess(
                'Request Submitted',
                'Your withdrawal request has been submitted successfully.',
                () => navigate('/profile')
            );
        } catch (error) {
            alert.showError('Error', error.message || 'Failed to submit withdrawal request');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <LoadingOverlay visible message="Loading details..." />;
    }

    return (
        <div className="withdrawal-premium-screen">
            <div className="withdraw-header-nav">
                <button className="back-btn-withdraw" onClick={() => navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="withdraw-title-nav">APPLY WITHDRAWAL</h1>
            </div>

            <div className="withdraw-content-area">
                {/* Available Balance Card */}
                <div className="withdrawable-balance-card">
                    <span className="balance-label-top">WITHDRAWABLE BALANCE</span>
                    <h2 className="balance-value-top">{formatCurrency(userData?.withdrawBalance || 0)}</h2>
                </div>

                <div className="withdraw-form-container">
                    <div className="withdraw-input-group">
                        <label className="withdraw-field-label">WITHDRAWAL AMOUNT</label>
                        <div className="amount-input-box">
                            <span className="currency-label">₹</span>
                            <input
                                type="number"
                                className="main-amount-input"
                                placeholder="0"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="received-amount-card">
                        <span className="received-label">AMOUNT TO BE RECEIVED:</span>
                        <span className="received-value">{formatCurrency(calculateNetAmount())}</span>
                    </div>

                    <p className="tax-notice-text">FEE: {WITHDRAWAL_TAX}% TAX WILL BE DEDUCTED</p>

                    <div className="receiving-bank-section">
                        <div className="bank-section-header">
                            <span className="bank-header-title">
                                <span className="material-icons">account_balance</span>
                                RECEIVING BANK
                            </span>
                            <button className="change-bank-btn" onClick={() => navigate('/bank-details')}>
                                {bankDetails ? 'CHANGE' : 'ADD'}
                            </button>
                        </div>

                        <div className="bank-card-content">
                            {bankDetails ? (
                                <div className="bank-info-display">
                                    <span className="bank-display-name">{bankDetails.bankName}</span>
                                    <span className="bank-display-acc">{maskAccountNumber(bankDetails.accountNumber)}</span>
                                </div>
                            ) : (
                                <button className="add-bank-prompt" onClick={() => navigate('/bank-details')}>
                                    + ADD BANK ACCOUNT FIRST
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="withdraw-input-group">
                        <label className="withdraw-field-label">WITHDRAWAL PASSWORD</label>
                        <input
                            type="password"
                            className="pin-password-input"
                            placeholder="Enter PIN"
                            value={fundPassword}
                            onChange={(e) => setFundPassword(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        />
                    </div>

                    <button
                        className="submit-withdrawal-btn"
                        onClick={handleWithdraw}
                        disabled={submitting}
                    >
                        {submitting ? 'PROCESSING...' : 'SUBMIT WITHDRAWAL'}
                    </button>
                </div>

                {/* Rules Section */}
                <div className="withdrawal-rules-premium">
                    <div className="rules-header-top">
                        <span className="material-icons">format_list_bulleted</span>
                        WITHDRAWAL RULES
                    </div>
                    <div className="rules-list-items">
                        <div className="rule-premium-item">
                            <span className="rule-index">01.</span>
                            <span className="rule-description">MINIMUM WITHDRAWAL AMOUNT IS {formatCurrency(MIN_WITHDRAWAL)}.</span>
                        </div>
                        <div className="rule-premium-item">
                            <span className="rule-index">02.</span>
                            <span className="rule-description">ONLY 1 WITHDRAWAL ALLOWED PER DAY.</span>
                        </div>
                        <div className="rule-premium-item">
                            <span className="rule-index">03.</span>
                            <span className="rule-description">WITHDRAWAL TIME: MONDAY TO SUNDAY, 24×7 ANYTIME.</span>
                        </div>
                        <div className="rule-premium-item">
                            <span className="rule-index">04.</span>
                            <span className="rule-description">A SERVICE TAX OF {WITHDRAWAL_TAX}% WILL BE DEDUCTED PER WITHDRAWAL.</span>
                        </div>
                        <div className="rule-premium-item">
                            <span className="rule-index">05.</span>
                            <span className="rule-description">PAYMENT ARRIVES WITHIN 24 TO 48 HOURS.</span>
                        </div>
                        <div className="rule-premium-item">
                            <span className="rule-index">06.</span>
                            <span className="rule-description">PLEASE ENSURE BANK DETAILS ARE ACCURATE BEFORE SUBMITTING.</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WithdrawScreen;
